/**
 * ═══════════════════════════════════════════════════════════════════════════
 * BACKEND SERVICE v3.0.0 - ОБЪЕДИНЁННАЯ ВЕРСИЯ
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Путь: frontend/src/services/BackendService.ts
 * Дата: 07.01.2026
 *
 * ИСТОРИЯ ИЗМЕНЕНИЙ:
 * v1.x.x - Production версия (340 строк)
 * v2.0.0 - Первоначальная реализация training mode
 * v2.1.0 - GPT аудит: полная изоляция CRUD операций
 * v3.0.0 - ОБЪЕДИНЕНИЕ: Production + Training Mode
 *
 * КРИТИЧЕСКИЕ ВОЗМОЖНОСТИ v3.0.0:
 * ✅ ВСЯ production функциональность сохранена (340 строк базы)
 * ✅ Training Mode API с полной изоляцией
 * ✅ getRequest() автопереключается на training API
 * ✅ getTrainingRequest(id) для получения заявки по ID
 * ✅ updateRequest() / deleteRequest() с изоляцией
 * ✅ generateTestSignal() НЕ использует fallback в training mode
 * ✅ Все 14 точек изоляции _trainingMode
 *
 * СТРОК: ~620
 */

/* eslint-disable @typescript-eslint/no-explicit-any */
import { io, Socket } from 'socket.io-client';
import { API_BASE_URL } from '../config/paths';

// ═══════════════════════════════════════════════════════════════════════════
// КОНФИГУРАЦИЯ
// ═══════════════════════════════════════════════════════════════════════════

// Поддерживаем все известные варианты имени события на случай различий между бэкендами
const SOCKET_SIGNAL_EVENTS = ['signal', 'new_signal', 'new-signal', 'signal:new'];

// ═══════════════════════════════════════════════════════════════════════════
// TRAINING MODE STATE
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Флаг тренировочного режима
 * КРИТИЧНО: Все CRUD операции проверяют этот флаг для изоляции данных
 */
let _trainingMode = false;

// ═══════════════════════════════════════════════════════════════════════════
// ВСПОМОГАТЕЛЬНЫЕ ФУНКЦИИ
// ═══════════════════════════════════════════════════════════════════════════

type Query = Record<string, string | number | boolean | undefined | null>;

function buildQuery(params?: Query): string {
  if (!params) return '';
  const sp = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v === undefined || v === null) return;
    sp.set(k, String(v));
  });
  const s = sp.toString();
  return s ? `?${s}` : '';
}

/**
 * Получить токен авторизации
 * КРИТИЧНО: AuthContext.tsx сохраняет в sessionStorage.token!
 */
function getAuthToken(): string | null {
  // Проверяем sessionStorage ПЕРВЫМ (основное хранилище)
  const sessionToken = sessionStorage.getItem('token') || sessionStorage.getItem('authToken');
  if (sessionToken) return sessionToken;

  // Fallback на localStorage (для совместимости)
  const token = localStorage.getItem('token') || localStorage.getItem('authToken');
  return token || null;
}

/**
 * Базовый fetch с обработкой ошибок и авторизацией
 */
async function request<T>(url: string, init?: RequestInit & { timeoutMs?: number }): Promise<T> {
  const controller = new AbortController();
  const t = setTimeout(() => controller.abort(), init?.timeoutMs ?? 30000);

  // Нормализация любых headers → Record<string,string>
  const normalizeHeaders = (h?: RequestInit['headers']): Record<string, string> => {
    if (!h) return {};
    if (h instanceof Headers) {
      const out: Record<string, string> = {};
      h.forEach((v, k) => (out[k] = v));
      return out;
    }
    if (Array.isArray(h)) {
      return h.reduce(
        (acc, [k, v]) => {
          acc[String(k)] = String(v);
          return acc;
        },
        {} as Record<string, string>
      );
    }
    const obj = h as Record<string, unknown>;
    return Object.fromEntries(Object.entries(obj).map(([k, v]) => [k, String(v)]));
  };

  try {
    const token = getAuthToken();

    // 1) Разбираем init на headers и прочее
    const { headers: initHeaders, ...rest } = init ?? {};

    // 2) Порядок: базовые → нормализованные из init → наши обязательно-нужные
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...normalizeHeaders(initHeaders),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };

    const res = await fetch(url, {
      // Разрешаем переопределять что угодно из init,
      // но уже с НОРМАЛИЗОВАННЫМИ заголовками
      ...rest,
      credentials: 'include',
      signal: controller.signal,
      headers,
    });

    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new Error(`HTTP ${res.status} ${res.statusText}: ${text}`);
    }

    const ct = res.headers.get('content-type') || '';
    if (ct.includes('application/json')) return (await res.json()) as T;
    return (await res.text()) as unknown as T;
  } finally {
    clearTimeout(t);
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// BACKEND SERVICE
// ═══════════════════════════════════════════════════════════════════════════

export const BackendService = {
  // ═══════════════════════════════════════════════════════════════════════
  // TRAINING MODE MANAGEMENT
  // ═══════════════════════════════════════════════════════════════════════

  /**
   * Включить/выключить тренировочный режим
   */
  setTrainingMode(enabled: boolean) {
    _trainingMode = enabled;
    console.log(`[BackendService] Training mode: ${enabled ? 'ON' : 'OFF'}`);
  },

  /**
   * Проверить, включён ли тренировочный режим
   */
  isTrainingMode(): boolean {
    return _trainingMode;
  },

  // ═══════════════════════════════════════════════════════════════════════
  // TRAINING MODE API - Заявки
  // ═══════════════════════════════════════════════════════════════════════

  /**
   * Получить список тренировочных заявок
   */
  getTrainingRequests(params?: Query) {
    return request<any>(`${API_BASE_URL}/training/requests${buildQuery(params)}`, {
      method: 'GET',
    });
  },

  /**
   * Получить тренировочную заявку по ID
   * ДОБАВЛЕНО в v2.1.0 по результатам GPT аудита
   */
  getTrainingRequest(id: string | number) {
    return request<any>(`${API_BASE_URL}/training/requests/${encodeURIComponent(id)}`, {
      method: 'GET',
    });
  },

  /**
   * Создать тренировочную заявку
   */
  createTrainingRequest(payload: Record<string, any>) {
    return request<any>(`${API_BASE_URL}/training/requests`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  /**
   * Обновить тренировочную заявку
   */
  updateTrainingRequest(id: string | number, payload: Record<string, any>) {
    return request<any>(`${API_BASE_URL}/training/requests/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  },

  /**
   * Удалить тренировочную заявку
   */
  deleteTrainingRequest(id: string | number) {
    return request<any>(`${API_BASE_URL}/training/requests/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
  },

  // ═══════════════════════════════════════════════════════════════════════
  // TRAINING MODE API - Сигналы
  // ═══════════════════════════════════════════════════════════════════════

  /**
   * Получить список тренировочных сигналов
   */
  getTrainingSignals(params?: Query) {
    return request<any>(`${API_BASE_URL}/training/signals${buildQuery(params)}`, { method: 'GET' });
  },

  /**
   * Создать тренировочный сигнал
   */
  createTrainingSignal(payload: Record<string, any>) {
    return request<any>(`${API_BASE_URL}/training/signals`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  /**
   * Удалить тренировочный сигнал
   */
  deleteTrainingSignal(id: string | number) {
    return request<any>(`${API_BASE_URL}/training/signals/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
  },

  // ═══════════════════════════════════════════════════════════════════════
  // TRAINING MODE API - Управление
  // ═══════════════════════════════════════════════════════════════════════

  /**
   * Получить статистику тренировочного режима
   */
  getTrainingStats() {
    return request<any>(`${API_BASE_URL}/training/stats`, { method: 'GET' });
  },

  /**
   * Сбросить тренировочные данные
   */
  resetTrainingData() {
    return request<any>(`${API_BASE_URL}/training/reset`, { method: 'POST' });
  },

  /**
   * Заполнить тренировочные данные начальными значениями
   */
  seedTrainingData() {
    return request<any>(`${API_BASE_URL}/training/seed`, { method: 'POST' });
  },

  // ═══════════════════════════════════════════════════════════════════════
  // ЗАЯВКИ (с изоляцией training mode)
  // ═══════════════════════════════════════════════════════════════════════

  /**
   * Получить список заявок
   * АВТОПЕРЕКЛЮЧЕНИЕ: training mode → /api/training/requests
   */
  getRequests(params?: Query) {
    if (_trainingMode) {
      return this.getTrainingRequests(params);
    }
    return request<any>(`${API_BASE_URL}/requests${buildQuery(params)}`, { method: 'GET' });
  },

  /**
   * Получить заявку по ID
   * АВТОПЕРЕКЛЮЧЕНИЕ: training mode → /api/training/requests/:id
   * ДОБАВЛЕНО в v2.1.0: изоляция для getRequest
   */
  getRequest(id: string | number) {
    if (_trainingMode) {
      return this.getTrainingRequest(id);
    }
    return request<any>(`${API_BASE_URL}/requests/${encodeURIComponent(id)}`, { method: 'GET' });
  },

  /**
   * Создать заявку
   * АВТОПЕРЕКЛЮЧЕНИЕ: training mode → /api/training/requests
   */
  createRequest(payload: Record<string, any>) {
    if (_trainingMode) {
      return this.createTrainingRequest(payload);
    }
    return request<any>(`${API_BASE_URL}/requests`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  /**
   * Обновить заявку
   * АВТОПЕРЕКЛЮЧЕНИЕ: training mode → /api/training/requests/:id
   * ДОБАВЛЕНО в v2.1.0
   */
  updateRequest(id: string | number, payload: Record<string, any>) {
    if (_trainingMode) {
      return this.updateTrainingRequest(id, payload);
    }
    return request<any>(`${API_BASE_URL}/requests/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  },

  /**
   * Удалить заявку
   * АВТОПЕРЕКЛЮЧЕНИЕ: training mode → /api/training/requests/:id
   * ДОБАВЛЕНО в v2.1.0
   */
  deleteRequest(id: string | number) {
    if (_trainingMode) {
      return this.deleteTrainingRequest(id);
    }
    return request<any>(`${API_BASE_URL}/requests/${encodeURIComponent(id)}`, { method: 'DELETE' });
  },

  /**
   * Получить МОИ заявки
   */
  getMyRequests(params?: Query) {
    return request<any>(`${API_BASE_URL}/requests/my${buildQuery(params)}`, { method: 'GET' });
  },

  // ═══════════════════════════════════════════════════════════════════════
  // СИГНАЛЫ (с изоляцией training mode)
  // ═══════════════════════════════════════════════════════════════════════

  /**
   * Получить список сигналов
   * АВТОПЕРЕКЛЮЧЕНИЕ: training mode → /api/training/signals
   */
  getSignals(params?: Query) {
    if (_trainingMode) {
      return this.getTrainingSignals(params);
    }
    return request<any>(`${API_BASE_URL}/signals${buildQuery(params)}`, { method: 'GET' });
  },

  /**
   * Получить статистику сигналов
   */
  getSignalStatistics(params?: Query) {
    return request<any>(`${API_BASE_URL}/signals/statistics${buildQuery(params)}`, {
      method: 'GET',
    });
  },

  /**
   * Создать сигнал
   * АВТОПЕРЕКЛЮЧЕНИЕ: training mode → /api/training/signals
   */
  createSignal(payload: Record<string, any>) {
    if (_trainingMode) {
      return this.createTrainingSignal(payload);
    }
    return request<any>(`${API_BASE_URL}/signals`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  /**
   * Обновить сигнал
   * ИЗОЛЯЦИЯ: В training mode операция блокируется (нет training endpoint)
   */
  updateSignal(id: string, payload: Record<string, any>) {
    if (_trainingMode) {
      console.warn('[BackendService] updateSignal blocked in training mode');
      return Promise.resolve({ success: false, message: 'Operation blocked in training mode' });
    }
    return request<any>(`${API_BASE_URL}/signals/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  },

  /**
   * Удалить сигнал
   * АВТОПЕРЕКЛЮЧЕНИЕ: training mode → /api/training/signals/:id
   */
  deleteSignal(id: string) {
    if (_trainingMode) {
      return this.deleteTrainingSignal(id);
    }
    return request<any>(`${API_BASE_URL}/signals/${encodeURIComponent(id)}`, { method: 'DELETE' });
  },

  /**
   * Связать сигнал с заявкой
   * ИЗОЛЯЦИЯ: В training mode операция блокируется
   */
  linkSignalToRequest(signalId: string, requestId: string) {
    if (_trainingMode) {
      console.warn('[BackendService] linkSignalToRequest blocked in training mode');
      return Promise.resolve({ success: false, message: 'Operation blocked in training mode' });
    }
    return request<any>(
      `${API_BASE_URL}/signals/${encodeURIComponent(signalId)}/link/${encodeURIComponent(requestId)}`,
      { method: 'POST' }
    );
  },

  /**
   * Сгенерировать отчёт по сигналу
   */
  generateSignalReport(id: string) {
    return request<any>(`${API_BASE_URL}/signals/generate-report/${encodeURIComponent(id)}`, {
      method: 'POST',
    });
  },

  // ═══════════════════════════════════════════════════════════════════════
  // ТЕРМИНАЛЫ
  // ═══════════════════════════════════════════════════════════════════════

  getTerminals(params?: Query) {
    return request<any>(`${API_BASE_URL}/terminals${buildQuery(params)}`, { method: 'GET' });
  },

  createTerminal(payload: Record<string, any>) {
    return request<any>(`${API_BASE_URL}/terminals`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  updateTerminal(id: string, payload: Record<string, any>) {
    return request<any>(`${API_BASE_URL}/terminals/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  },

  deleteTerminal(id: string) {
    return request<any>(`${API_BASE_URL}/terminals/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
  },

  // ═══════════════════════════════════════════════════════════════════════
  // PDF
  // ═══════════════════════════════════════════════════════════════════════

  async getRequestPdf(id: string): Promise<ArrayBuffer> {
    const token = getAuthToken();
    const headers: Record<string, string> = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const res = await fetch(`${API_BASE_URL}/requests/${encodeURIComponent(id)}/pdf`, {
      method: 'GET',
      credentials: 'include',
      headers,
    });
    if (!res.ok) throw new Error(`HTTP ${res.status} ${res.statusText}`);
    return await res.arrayBuffer();
  },

  async generatePdf(path: string, payload: Record<string, any>): Promise<ArrayBuffer> {
    const token = getAuthToken();
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const res = await fetch(`${API_BASE_URL}${path}`, {
      method: 'POST',
      credentials: 'include',
      headers,
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status} ${res.statusText}`);
    return await res.arrayBuffer();
  },

  // ═══════════════════════════════════════════════════════════════════════
  // EMAIL / УВЕДОМЛЕНИЯ
  // ═══════════════════════════════════════════════════════════════════════

  sendNotificationEmail(payload: { to: string; subject: string; body: string }) {
    return request<any>(`${API_BASE_URL}/notifications/email`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  getPendingEmailLogs(params?: Query) {
    return request<any>(`${API_BASE_URL}/email-logs/pending${buildQuery(params)}`, {
      method: 'GET',
    });
  },

  registerEmailAsRequest(id: string | number, payload: Record<string, any>) {
    return request<any>(
      `${API_BASE_URL}/email-logs/${encodeURIComponent(id)}/register-as-request`,
      {
        method: 'POST',
        body: JSON.stringify(payload),
      }
    );
  },

  registerEmailAsSignal(id: string | number, payload: Record<string, any>) {
    return request<any>(`${API_BASE_URL}/email-logs/${encodeURIComponent(id)}/register-as-signal`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  rejectEmailLog(id: string | number, payload: Record<string, any>) {
    return request<any>(`${API_BASE_URL}/email-logs/${encodeURIComponent(id)}/reject`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  markEmailService(id: string | number, payload: Record<string, any>) {
    return request<any>(`${API_BASE_URL}/email-logs/${encodeURIComponent(id)}/mark-service`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  // ═══════════════════════════════════════════════════════════════════════
  // АВТОРИЗАЦИЯ
  // ═══════════════════════════════════════════════════════════════════════

  login(email: string, password: string) {
    return request<any>(`${API_BASE_URL}/auth/login`, {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
  },

  logout() {
    return request<any>(`${API_BASE_URL}/auth/logout`, { method: 'POST' });
  },

  setAuthToken(token: string) {
    // КРИТИЧНО: AuthContext использует sessionStorage!
    sessionStorage.setItem('token', token);
    // Дублируем в localStorage для совместимости
    localStorage.setItem('token', token);
  },

  clearAuthToken() {
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('authToken');
    sessionStorage.removeItem('refreshToken');
    localStorage.removeItem('token');
    localStorage.removeItem('authToken');
  },

  // ═══════════════════════════════════════════════════════════════════════
  // SOCKET.IO
  // ═══════════════════════════════════════════════════════════════════════

  createSocket(extra?: Parameters<typeof io>[1]): Socket {
    return io('/', {
      path: '/socket.io',
      transports: ['websocket', 'polling'],
      withCredentials: true,
      autoConnect: true,
      ...extra,
    });
  },

  initWebSocket(onSignal?: (payload: any) => void, extra?: Parameters<typeof io>[1]): Socket {
    const socket = this.createSocket(extra);
    if (onSignal) {
      SOCKET_SIGNAL_EVENTS.forEach((evt) => {
        socket.on(evt, onSignal);
      });
    }
    return socket;
  },

  disconnectSocket(sock?: Socket) {
    const s = sock as Socket | undefined;
    if (s && s.connected) s.disconnect();
  },

  // ═══════════════════════════════════════════════════════════════════════
  // ДОП. ДЕЙСТВИЯ / ТАСКИ
  // ═══════════════════════════════════════════════════════════════════════

  /**
   * Сгенерировать тестовый сигнал
   * КРИТИЧНО: В training mode НЕ использует fallback /testing/simulate
   * Это предотвращает утечку данных в production процессы
   */
  async generateTestSignal(payload?: Record<string, any>) {
    const defaults = {
      terminal_number: 'TEST-TERMINAL',
      signal_type: 'TEST',
      received_at: new Date().toISOString(),
      is_test: true,
      vessel_name: 'Тестовое судно',
      mmsi: '000000000',
    };

    // КРИТИЧНО: В training mode НЕ используем fallback /testing/simulate
    // Это предотвращает утечку в production-процессы
    if (_trainingMode) {
      return await this.createSignal({ ...defaults, ...(payload || {}) });
    }

    // Production: основной путь + fallback
    try {
      return await this.createSignal({ ...defaults, ...(payload || {}) });
    } catch (primaryError) {
      console.warn(
        'generateTestSignal via createSignal failed, trying /testing/simulate',
        primaryError
      );
      try {
        return await request<any>(`${API_BASE_URL}/testing/simulate`, {
          method: 'POST',
          body: JSON.stringify({ ...defaults, ...(payload || {}) }),
        });
      } catch (fallbackError) {
        console.error('generateTestSignal fallback failed', fallbackError);
        throw fallbackError;
      }
    }
  },

  async processEmailQueue() {
    try {
      return await request<any>(`${API_BASE_URL}/tasks/email/process`, { method: 'POST' });
    } catch (error) {
      console.warn('processEmailQueue endpoint unavailable, using stub.', error);
      return { success: false, message: 'Email queue processing not available in this build.' };
    }
  },

  async syncWithExternal() {
    try {
      return await request<any>(`${API_BASE_URL}/tasks/integration/sync`, { method: 'POST' });
    } catch (error) {
      console.warn('syncWithExternal endpoint unavailable, using stub.', error);
      return { success: false, message: 'External sync not available in this build.' };
    }
  },

  async runSystemCheck() {
    try {
      return await request<any>(`${API_BASE_URL}/tasks/system/check`, { method: 'POST' });
    } catch (error) {
      console.warn('runSystemCheck endpoint unavailable, using stub.', error);
      return { success: false, message: 'System check not available in this build.' };
    }
  },

  // ═══════════════════════════════════════════════════════════════════════
  // HEALTH CHECK
  // ═══════════════════════════════════════════════════════════════════════

  getHealth(): Promise<{ status: string }> {
    return request<{ status: string }>(`${API_BASE_URL}/health`, { method: 'GET' });
  },
};

export default BackendService;
